import { useRef, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useBlocker } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  articlesApi,
  isApiError,
  type ArticleDetail,
  type CreateArticleBody,
  type UpdateArticleBody,
} from '@shared/api';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardFooter,
  ConfirmDialog,
  Heading,
  Input,
  Label,
  PopoverSelect,
  PageBackLink,
  RequiredMark,
  Text,
  Textarea,
} from '@shared/ui';
import { articleKeys } from '../api/queries';
import {
  articleErrorKey,
  articleOutcomeUnknown,
  articleStatusLabel,
  articleTopicLabel,
} from '../model/presentation';
import { ArticleError } from './ArticleError';
import { ArticleTitleAction } from './ArticleTitleAction';
import { ArticleReplacementReview } from './ArticleReplacementReview';
import type { ArticleTopicsState } from './ArticleList';

type Draft = { title: string; content: string; topicId: string; status: string };
const emptyDraft: Draft = { title: '', content: '', topicId: '', status: 'Draft' };
const toDraft = (detail?: ArticleDetail): Draft =>
  detail
    ? {
        title: detail.data.title ?? '',
        content: detail.data.content ?? '',
        topicId: detail.data.topic?.id ?? '',
        status: detail.data.status ?? '',
      }
    : emptyDraft;
const changedFields = (draft: Draft, original: Draft) =>
  Object.fromEntries(
    Object.entries(draft).filter(([key, value]) => value !== original[key as keyof Draft])
  ) as Partial<Draft>;

type SaveIntent = {
  body: CreateArticleBody | UpdateArticleBody;
  id?: string;
  etag?: string;
  key: string;
};

/** One editor session owns the immutable write intent until its outcome is known. */
export function ArticleEditor({
  scope,
  initial,
  topics,
  onCancel,
  onBack,
  onSaved,
  embedded = false,
  canSave = true,
}: {
  scope: string;
  embedded?: boolean;
  canSave?: boolean;
  initial?: ArticleDetail;
  topics: ArticleTopicsState;
  onCancel: () => void;
  onBack?: () => void;
  onSaved: (detail: ArticleDetail) => void;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [base, setBase] = useState(initial);
  const [draft, setDraft] = useState(() => toDraft(initial));
  const [draftRevision, setDraftRevision] = useState(0);
  const [replacementDecision, setReplacementDecision] = useState<'replace' | 'keep_existing'>();
  const [replacementRequired, setReplacementRequired] = useState(false);
  const [latest, setLatest] = useState<ArticleDetail>();
  const [error, setError] = useState<unknown>();
  const [conflictError, setConflictError] = useState<unknown>();
  const [latestError, setLatestError] = useState<unknown>();
  const [invalid, setInvalid] = useState<Partial<Record<keyof Draft, boolean>>>({});
  const formRef = useRef<HTMLFormElement>(null);
  const [uncertain, setUncertain] = useState(false);
  const [savedId, setSavedId] = useState<string>();
  const [loadingLatest, setLoadingLatest] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const intent = useRef<SaveIntent | null>(null);
  const saving = useRef(false);
  const acknowledgedId = useRef<string | undefined>(undefined);
  const [working, setWorking] = useState(false);
  const allowLeave = useRef(false);
  const dirty = Object.keys(changedFields(draft, toDraft(base))).length > 0;
  const conflict = Boolean(conflictError);
  const unavailable = isApiError(error) && [401, 403, 404].includes(error.status);
  const blocker = useBlocker({
    shouldBlockFn: () =>
      !allowLeave.current && !acknowledgedId.current && (dirty || uncertain || saving.current),
    enableBeforeUnload: !savedId && (dirty || uncertain),
    withResolver: true,
  });
  const save = useMutation({
    retry: false,
    mutationFn: async (command: SaveIntent) => {
      const response = command.id
        ? await articlesApi.update(command.id, command.body, command.etag ?? '', command.key)
        : await articlesApi.create(command.body as CreateArticleBody, command.key);
      acknowledgedId.current = response.data.id;
      setSavedId(response.data.id);
      toast.success(t('articles.save.saved'));
      // Reading after admission is separate: a failed GET must never resubmit the write.
      return response.data.id;
    },
  });

  const readSaved = async (id: string) => {
    const detail = await articlesApi.detail(id);
    client.setQueryData(articleKeys.detail(scope, id), detail);
    void client.invalidateQueries({ queryKey: articleKeys.lists(scope) });
    allowLeave.current = true;
    onSaved(detail);
  };
  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (saving.current || (!acknowledgedId.current && (!canSave || unavailable || conflict)))
      return;
    setInvalid({});
    let command = intent.current;
    if (!command && !savedId) {
      if (
        base?.data.generation?.candidateArticleId &&
        base.data.status === 'Draft' &&
        draft.status === 'Approved' &&
        !replacementDecision
      ) {
        setReplacementRequired(true);
        return;
      }
      const changes = base ? changedFields(draft, toDraft(base)) : draft;
      const patch = {
        ...changes,
        ...(changes.topicId === undefined ? {} : { topicId: changes.topicId || null }),
        ...(base?.data.generation?.candidateArticleId &&
        base.data.status === 'Draft' &&
        draft.status === 'Approved' &&
        replacementDecision
          ? { replacementDecision }
          : {}),
      };
      const invalidFields = {
        title:
          patch.title !== undefined &&
          (patch.title.trim().length === 0 ||
            patch.title.trim().length > 500 ||
            patch.title.includes('\u0000')),
        content:
          patch.content !== undefined &&
          (!patch.content || patch.content.length > 100000 || patch.content.includes('\u0000')),
        topicId:
          patch.topicId != null && !topics.topics.some((topic) => topic.id === patch.topicId),
        status:
          patch.status !== undefined && patch.status !== 'Draft' && patch.status !== 'Approved',
      };
      const firstInvalid = (['title', 'content', 'topicId', 'status'] as const).find(
        (field) => invalidFields[field]
      );
      if (firstInvalid) {
        setInvalid(invalidFields);
        const control = formRef.current?.elements.namedItem(
          `article-${firstInvalid === 'topicId' ? 'topic' : firstInvalid}`
        );
        if (control instanceof HTMLElement) control.focus();
        return;
      }
      if (!Object.keys(patch).length) return;
      command = {
        body: patch as UpdateArticleBody,
        id: base?.data.id,
        etag: base?.etag,
        key: crypto.randomUUID(),
      };
      intent.current = command;
    }
    saving.current = true;
    setDraftRevision((current) => current + 1);
    setWorking(true);
    setError(undefined);
    try {
      let id = acknowledgedId.current;
      if (!id) {
        if (!command) return;
        id = await save.mutateAsync(command);
      }
      setUncertain(false);
      await readSaved(id);
    } catch (cause) {
      setError(cause);
      if (isApiError(cause) && [409, 412, 428].includes(cause.status)) {
        setConflictError(cause);
      }
      // An acknowledged write is retained even if the follow-up GET fails.
      if (!acknowledgedId.current) {
        const unknown = articleOutcomeUnknown(cause);
        setUncertain(unknown);
        if (!unknown) intent.current = null;
      }
    } finally {
      saving.current = false;
      setWorking(false);
    }
  };
  const refresh = async () => {
    if (!base || loadingLatest) return;
    setLoadingLatest(true);
    setLatestError(undefined);
    try {
      setLatest(await articlesApi.detail(base.data.id));
    } catch (cause) {
      setLatestError(cause);
    } finally {
      setLoadingLatest(false);
    }
  };
  const chooseLatest = (keepDraft: boolean) => {
    if (!latest) return;
    const changes = changedFields(draft, toDraft(base));
    setDraft(keepDraft ? { ...toDraft(latest), ...changes } : toDraft(latest));
    setDraftRevision((current) => current + 1);
    setReplacementDecision(undefined);
    setBase(latest);
    setLatest(undefined);
    setError(undefined);
    setConflictError(undefined);
    setLatestError(undefined);
    intent.current = null;
  };
  const cancel = () => {
    allowLeave.current = true;
    onCancel();
  };
  const locked =
    !canSave ||
    working ||
    uncertain ||
    Boolean(savedId) ||
    unavailable ||
    Boolean(latest?.data.deletion);
  const fieldErrors = {
    title: invalid.title
      ? !draft.title.trim()
        ? t('common.validation.required')
        : draft.title.trim().length > 500
          ? t('common.validation.maxLength', { max: 500 })
          : t('common.validation.unsupportedCharacters')
      : undefined,
    content: invalid.content
      ? !draft.content
        ? t('common.validation.required')
        : draft.content.length > 100000
          ? t('common.validation.maxLength', { max: 100000 })
          : t('common.validation.unsupportedCharacters')
      : undefined,
    topicId: invalid.topicId
      ? t(draft.topicId ? 'common.validation.choice' : 'common.validation.required')
      : undefined,
    status: invalid.status
      ? t(draft.status ? 'common.validation.choice' : 'common.validation.required')
      : undefined,
  };
  const needsTopics = Boolean(
    draft.topicId && (!base || draft.topicId !== (base.data.topic?.id ?? ''))
  );
  const editor = (
    <>
      <form
        ref={formRef}
        noValidate
        onSubmit={(event) => {
          void submit(event);
        }}
      >
        <CardContent className={initial ? 'min-w-0 space-y-4 p-4' : 'min-w-0 space-y-6 p-6'}>
          {initial ? (
            <Heading as="h2" size="md">
              {t('articles.information')}
            </Heading>
          ) : null}
          {!initial ? <Heading as="h1">{t('articles.create')}</Heading> : null}
          <div className="space-y-1">
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
              <Label htmlFor="article-title">
                {t('articles.fields.title')}
                <RequiredMark />
              </Label>
              <ArticleTitleAction
                articleId={base?.data.id}
                content={draft.content}
                title={draft.title}
                revision={draftRevision}
                disabled={locked || conflict}
                onTitle={(title) => {
                  setDraft((current) => ({ ...current, title }));
                  setDraftRevision((current) => current + 1);
                  setInvalid((current) => ({ ...current, title: false }));
                }}
              />
            </div>
            <Input
              id="article-title"
              value={draft.title}
              maxLength={500}
              required
              aria-required="true"
              disabled={locked}
              aria-invalid={invalid.title || undefined}
              aria-describedby={invalid.title ? 'article-title-error' : undefined}
              onChange={(event) => {
                setDraft({ ...draft, title: event.target.value });
                setDraftRevision((current) => current + 1);
                setInvalid((current) => ({ ...current, title: false }));
              }}
            />
            {fieldErrors.title ? (
              <Text id="article-title-error" role="alert" size="sm" color="destructive">
                {fieldErrors.title}
              </Text>
            ) : null}
          </div>
          <div className="space-y-1">
            <Label htmlFor="article-content">
              {t('articles.fields.content')}
              <RequiredMark />
            </Label>
            <Textarea
              id="article-content"
              value={draft.content}
              rows={6}
              maxLength={100000}
              required
              aria-required="true"
              disabled={locked}
              aria-invalid={invalid.content || undefined}
              aria-describedby={invalid.content ? 'article-content-error' : undefined}
              onChange={(event) => {
                setDraft({ ...draft, content: event.target.value });
                setDraftRevision((current) => current + 1);
                setInvalid((current) => ({ ...current, content: false }));
              }}
            />
            {fieldErrors.content ? (
              <Text id="article-content-error" role="alert" size="sm" color="destructive">
                {fieldErrors.content}
              </Text>
            ) : null}
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="article-topic">{t('articles.fields.topic')}</Label>
              <PopoverSelect
                id="article-topic"
                triggerAriaLabel={t('articles.fields.topic')}
                aria-invalid={invalid.topicId || undefined}
                aria-describedby={
                  [
                    invalid.topicId && 'article-topic-error',
                    topics.error && 'article-topic-options-error',
                  ]
                    .filter(Boolean)
                    .join(' ') || undefined
                }
                value={draft.topicId}
                disabled={locked}
                loading={topics.isPending}
                loadingLabel={t('common.loading')}
                emptyText=""
                onValueChange={(topicId) => {
                  setDraft({ ...draft, topicId });
                  setInvalid((current) => ({ ...current, topicId: false }));
                }}
                options={[
                  { value: '', label: t('crm.missingValue') },
                  ...(base?.data.topic &&
                  !topics.topics.some((topic) => topic.id === base.data.topic?.id)
                    ? [
                        {
                          value: base.data.topic.id,
                          label: articleTopicLabel(base.data.topic, t),
                        },
                      ]
                    : []),
                  ...topics.topics.map((topic) => ({
                    value: topic.id,
                    label: articleTopicLabel(topic, t),
                  })),
                ]}
              />
              {fieldErrors.topicId ? (
                <Text id="article-topic-error" role="alert" size="sm" color="destructive">
                  {fieldErrors.topicId}
                </Text>
              ) : null}
              {topics.error ? (
                <Alert id="article-topic-options-error" variant="destructive">
                  <AlertTitle>{t('articles.errors.topics')}</AlertTitle>
                  <Button type="button" variant="outline" onClick={topics.onRetry}>
                    {t('auth.retry')}
                  </Button>
                </Alert>
              ) : null}
            </div>
            <div className="space-y-1">
              <Label htmlFor="article-status">
                {t('articles.fields.status')}
                <RequiredMark />
              </Label>
              <PopoverSelect
                id="article-status"
                triggerAriaLabel={t('articles.fields.status')}
                aria-invalid={invalid.status || undefined}
                aria-describedby={invalid.status ? 'article-status-error' : undefined}
                value={draft.status}
                disabled={locked}
                required
                emptyText=""
                onValueChange={(status) => {
                  setDraft({ ...draft, status });
                  setInvalid((current) => ({ ...current, status: false }));
                }}
                options={[
                  ...(!['Draft', 'Approved'].includes(draft.status)
                    ? [{ value: draft.status, label: articleStatusLabel(draft.status, t) }]
                    : []),
                  ...(['Draft', 'Approved'] as const).map((status) => ({
                    value: status,
                    label: articleStatusLabel(status, t),
                  })),
                ]}
              />
              {fieldErrors.status ? (
                <Text id="article-status-error" role="alert" size="sm" color="destructive">
                  {fieldErrors.status}
                </Text>
              ) : null}
            </div>
          </div>
          {base?.data.generation?.candidateArticleId && base.data.status === 'Draft' ? (
            <ArticleReplacementReview
              scope={scope}
              candidateId={base.data.generation.candidateArticleId}
              approving={draft.status === 'Approved'}
              decision={replacementDecision}
              required={replacementRequired}
              disabled={locked}
              onDecision={(value) => {
                setReplacementDecision(value);
                setReplacementRequired(false);
              }}
            />
          ) : null}
          {!canSave ? (
            <Alert variant="destructive">
              <AlertTitle>{t('articles.errors.denied')}</AlertTitle>
            </Alert>
          ) : conflict ? (
            <Alert>
              <AlertTitle>{t(articleErrorKey(conflictError))}</AlertTitle>
              <AlertDescription className="space-y-3">
                {latestError ? <Text size="sm">{t(articleErrorKey(latestError))}</Text> : null}
                {latest ? (
                  <section className="space-y-2" aria-label={t('articles.conflict.latest')}>
                    <Heading as="h2" size="sm">
                      {t('articles.conflict.latest')}
                    </Heading>
                    <Text size="sm" className="break-words">
                      {latest.data.title}
                    </Text>
                    <Text
                      size="sm"
                      className="max-h-48 overflow-auto whitespace-pre-wrap break-words"
                    >
                      {latest.data.content}
                    </Text>
                    <Text size="sm">
                      {articleTopicLabel(latest.data.topic, t)} ·{' '}
                      {articleStatusLabel(latest.data.status, t)}
                    </Text>
                    {latest.data.deletion ? (
                      <Text>{t(`articles.deletion.${latest.data.deletion.state}`)}</Text>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            chooseLatest(false);
                          }}
                        >
                          {t('articles.conflict.useLatest')}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => {
                            chooseLatest(true);
                          }}
                        >
                          {t('articles.conflict.keepDraft')}
                        </Button>
                      </div>
                    )}
                  </section>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    loading={loadingLatest}
                    onClick={() => {
                      void refresh();
                    }}
                  >
                    {t('articles.conflict.load')}
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          ) : uncertain ? (
            <Alert>
              <AlertTitle>{t('articles.save.unknown')}</AlertTitle>
              <AlertDescription>{t('articles.save.sameRetry')}</AlertDescription>
            </Alert>
          ) : savedId && error ? (
            <Alert>
              <AlertTitle>{t('articles.save.readPending')}</AlertTitle>
              <AlertDescription>{t(articleErrorKey(error))}</AlertDescription>
            </Alert>
          ) : error ? (
            <ArticleError error={error} />
          ) : null}
        </CardContent>
        <CardFooter
          className={
            initial
              ? 'flex-wrap justify-end gap-2 p-4 pt-0'
              : 'flex-wrap justify-end gap-2 p-6 pt-0'
          }
        >
          <Button
            type="button"
            variant="outline"
            disabled={working || uncertain}
            onClick={() => {
              if (dirty && !savedId) setCancelOpen(true);
              else cancel();
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            loading={working}
            disabled={
              working ||
              (!savedId && (!canSave || unavailable || conflict)) ||
              (!savedId &&
                !uncertain &&
                (!dirty || (needsTopics && (topics.isPending || Boolean(topics.error)))))
            }
          >
            {t(
              savedId
                ? 'articles.save.retryRead'
                : uncertain
                  ? 'articles.save.retrySame'
                  : 'common.save'
            )}
          </Button>
        </CardFooter>
      </form>
      <ConfirmDialog
        open={cancelOpen || blocker.status === 'blocked'}
        title={t('articles.discard.title')}
        description={t(uncertain ? 'articles.discard.pending' : 'articles.discard.description')}
        confirmLabel={t('articles.discard.confirm')}
        cancelLabel={t('common.cancel')}
        confirmButtonProps={{ disabled: uncertain || working }}
        onConfirm={() => {
          if (blocker.status === 'blocked') blocker.proceed();
          else cancel();
        }}
        onOpenChange={(open) => {
          if (!open) {
            setCancelOpen(false);
            if (blocker.status === 'blocked') blocker.reset();
          }
        }}
      />
    </>
  );
  if (embedded) return editor;
  return (
    <section
      className="flex min-w-0 flex-col gap-4"
      aria-label={t(base ? 'articles.edit' : 'articles.create')}
    >
      {onBack ? <PageBackLink onClick={onBack}>{t('articles.back')}</PageBackLink> : null}
      {initial ? (
        <Heading as="h1" size="lg" className="break-words">
          {initial.data.title ?? t('crm.missingValue')}
        </Heading>
      ) : null}
      <Card>{editor}</Card>
    </section>
  );
}
